/*
 * Gameplay components. Each game fills the space the room gives it
 * (a flex column) and never scrolls the page while a round is open.
 */
.play { flex: 1; min-height: 0; display: flex; flex-direction: column; gap: var(--s-3); }
.grow { flex: 1; min-height: 0; display: flex; flex-direction: column; justify-content: center; gap: var(--s-3); }
.hint { color: var(--dust); font-size: var(--t-sm); text-align: center; }
.small { font-size: var(--t-sm); color: var(--ash); }
.center { text-align: center; }
.growEnd { justify-content: flex-end; }
.muted { color: var(--ash); text-align: center; }
.inSodium { color: var(--sodium); }
.inStop { color: var(--stop); }
.inSpeed { color: var(--g-speedrun); }
.inAccent { color: var(--accent); }
.inFlood { color: var(--g-flood); }
.inWay { color: var(--g-wrong-way); }

/* every tappable game object answers the press the same way */
.can:active:not(:disabled), .suit:active:not(:disabled), .call:active:not(:disabled), .choice:active:not(:disabled), .key:active:not(:disabled) { transform: translateY(1px) scale(0.99); }
.can, .suit, .call, .choice, .key { transition: transform var(--m-fast) var(--ease), opacity var(--m-fast), border-color var(--m-fast), background var(--m-fast); }

/* action area pinned to the bottom of the game */
.action { display: grid; gap: var(--s-2); padding-top: var(--s-2); }
.locked {
  display: flex;
  background: rgba(189, 226, 228, 0.04);
  align-items: center;
  justify-content: center;
  gap: var(--s-2);
  min-height: var(--tap-lg);
  border: 1px dashed var(--fluoro-dim);
  color: var(--fluoro);
  font-family: var(--f-cond);
  font-weight: 700;
  font-size: var(--t-lg);
  text-align: center;
  padding: 0 var(--s-3);
}

.watching { border-color: var(--steel); color: var(--dust); background: transparent; }
.lockedGo { border-color: var(--go-dim); color: var(--go); background: rgba(134, 209, 122, 0.05); }
.lockedStop { border-color: var(--stop-dim); color: var(--stop); background: rgba(238, 75, 59, 0.05); }

/* ------------------------------------------------------------ guess the color: five paint cans */

.cans { flex: 1; min-height: 0; display: flex; flex-direction: column; gap: 6px; }
.can {
  flex: 1;
  min-height: 52px;
  max-height: 88px;
  display: grid;
  grid-template-columns: 30% 1fr auto;
  align-items: center;
  gap: var(--s-4);
  padding: 0 var(--s-4) 0 0;
  border: 1px solid var(--steel);
  background: var(--char);
  color: var(--chalk);
  text-align: left;
  transition: opacity var(--m-fast), border-color var(--m-fast), transform var(--m-fast) var(--ease);
  touch-action: manipulation;
}
.can:disabled { cursor: default; }
/* a brushed stripe of paint, with a dry right edge */
.swatch {
  height: 100%;
  background:
    linear-gradient(90deg, rgba(255, 255, 255, 0.12), transparent 30%, rgba(0, 0, 0, 0.18)),
    var(--paper-grain),
    var(--c);
  clip-path: polygon(0 0, 91% 0, 100% 10%, 94% 27%, 99% 44%, 92% 61%, 100% 79%, 95% 100%, 0 100%);
}
.canName { font-family: var(--f-display); font-weight: 800; font-size: var(--t-2xl); text-transform: uppercase; letter-spacing: 0.03em; }
.can[aria-pressed='true'] { border: 2px solid var(--chalk); background: var(--slab-hi); }
.cans[data-chosen='true'] .can[aria-pressed='false'] { opacity: 0.38; }
.can[data-win='true'] { border: 2px solid var(--c); box-shadow: 0 0 24px -6px var(--c); opacity: 1 !important; }
.can[data-win='false'] { opacity: 0.3; }
.count { font-family: var(--f-display); font-weight: 800; font-size: var(--t-xl); color: var(--dust); }
.cansTally { flex: none; }
.cansTally .can { min-height: 44px; max-height: 52px; }
.cansTally .canName { font-size: var(--t-xl); }

/* ------------------------------------------------------------ zero hour */

/* ------------------------------------------------------------ zero hour: a split-flap counter */

.formula { line-height: 1.2; text-align: center; font-family: var(--f-cond); font-weight: 600; font-size: var(--t-lg); color: var(--dust); }
.formula b { color: var(--g-zero-hour); font-family: var(--f-display); font-weight: 800; font-size: var(--t-2xl); }
.readout { text-align: center; display: grid; gap: var(--s-2); justify-items: center; }
.counter {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  width: min(100%, 264px);
  padding: 6px;
  background: var(--void);
  border: 1px solid var(--steel);
  box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.7), var(--edge);
}
.counter span {
  position: relative;
  display: grid;
  place-items: center;
  height: clamp(52px, 11dvh, 108px);
  background: linear-gradient(180deg, #23221e 0 49.4%, #0d0d0c 49.4% 50.6%, #191815 50.6% 100%);
  font-family: var(--f-display);
  font-weight: 800;
  font-size: clamp(2.6rem, 8dvh, 5rem);
  line-height: 1;
  color: var(--g-zero-hour);
  font-variant-numeric: tabular-nums;
}
.counter span[data-lead='true'] { color: #3b3933; }
.counter[data-locked='true'] span:not([data-lead='true']) { color: var(--chalk); }
.keypad { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
.key {
  min-height: clamp(44px, 7dvh, 56px);
  border: 1px solid var(--steel);
  background: var(--slab);
  font-family: var(--f-display);
  font-weight: 700;
  font-size: var(--t-2xl);
  color: var(--chalk);
  box-shadow: var(--edge);
  touch-action: manipulation;
}
.key:active:not(:disabled) { background: var(--slab-hi); }
.key:disabled { opacity: 0.4; }
.keyAlt { font-family: var(--f-cond); font-size: var(--t-md); color: var(--dust); }

.line { position: relative; height: 88px; margin: var(--s-6) var(--s-2) var(--s-2); }
.lineAxis { position: absolute; left: 0; right: 0; top: 44px; height: 2px; background: var(--grit); }
.tick { position: absolute; top: 34px; width: 2px; height: 20px; background: var(--dust); transform: translateX(-1px); }
.tickMe { background: var(--chalk); height: 28px; top: 30px; width: 3px; }
.tickWin { background: var(--go); height: 32px; top: 28px; width: 3px; }
.marker { position: absolute; transform: translateX(-50%); display: grid; justify-items: center; font-family: var(--f-cond); font-weight: 700; font-size: var(--t-sm); white-space: nowrap; }
.markerTarget { top: -6px; color: var(--sodium); }
.markerTarget::after { content: ''; width: 2px; height: 26px; background: var(--sodium); }
.markerAvg { top: 58px; color: var(--fluoro); }
.markerAvg::before { content: ''; width: 2px; height: 10px; background: var(--fluoro); }
.lineEnds { display: flex; justify-content: space-between; font-family: var(--f-cond); color: var(--ash); font-size: var(--t-sm); margin: 0 var(--s-2); }

/* ------------------------------------------------------------ trust yourself (high/low) */

.ticket {
  position: relative;
  align-self: center;
  width: min(100%, 300px);
  padding: var(--s-4) var(--s-5) var(--s-5);
  background: var(--chalk);
  color: var(--void);
  text-align: center;
  clip-path: polygon(0 0, 100% 0, 100% 40%, 96% 50%, 100% 60%, 100% 100%, 0 100%, 0 60%, 4% 50%, 0 40%);
}
.ticketLabel { font-family: var(--f-cond); font-weight: 700; font-size: var(--t-sm); letter-spacing: 0.04em; color: #4b4841; }
.ticketNum { font-family: var(--f-display); font-weight: 900; font-size: clamp(5rem, 28vw, 7.5rem); line-height: 0.85; }
.ticketFoot { font-family: var(--f-cond); font-weight: 600; font-size: var(--t-sm); color: #4b4841; }
.calls { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.call {
  display: grid;
  place-items: center;
  gap: 2px;
  min-height: 84px;
  border: 1px solid var(--steel);
  background: var(--slab);
  font-family: var(--f-display);
  font-weight: 800;
  font-size: var(--t-3xl);
  color: var(--chalk);
  line-height: 1;
  touch-action: manipulation;
}
.call small { font-family: var(--f-cond); font-weight: 600; font-size: var(--t-xs); color: var(--dust); }
.call[aria-pressed='true'] { border: 2px solid var(--go); background: var(--go-dim); }
.call:disabled { opacity: 0.45; }
.vs { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; text-align: center; gap: var(--s-3); }
.vsNum { font-family: var(--f-display); font-weight: 900; font-size: clamp(3.4rem, 18vw, 5rem); line-height: 0.9; }
.vsLabel { font-family: var(--f-cond); font-weight: 600; color: var(--ash); font-size: var(--t-sm); }

/* ------------------------------------------------------------ decode */

/* ------------------------------------------------------------ decode: paper letter tiles */

/* one tile = the width the longest word allows, capped at 46px */
.board { --tile: min(46px, calc((min(100vw, 560px) - 2 * var(--gutter) - (var(--n) - 1) * 4px) / var(--n))); display: flex; flex-wrap: wrap; justify-content: center; gap: 12px 16px; padding: var(--s-2) 0; }
.word { display: flex; gap: 4px; }
.tile {
  width: var(--tile);
  height: calc(var(--tile) * 1.22);
  display: grid;
  place-items: center;
  background: var(--paper-grain), linear-gradient(180deg, var(--paper-hi), var(--paper));
  color: var(--paper-ink);
  font-family: var(--f-display);
  font-weight: 800;
  font-size: calc(var(--tile) * 0.74);
  line-height: 1;
  box-shadow: inset 0 -3px 0 var(--paper-shade);
}
.tileBlank { background: var(--slab-hi); box-shadow: inset 0 -3px 0 var(--grit), var(--edge); }
.tileOpen { animation: flip var(--m-slow) var(--ease) both; animation-delay: calc(var(--i, 0) * 25ms); }
.category { display: grid; justify-items: center; gap: 2px; text-align: center; }
.category b { font-family: var(--f-display); font-weight: 800; font-size: var(--t-xl); text-transform: uppercase; color: var(--g-decode); line-height: 1; }
.category span { font-size: var(--t-sm); color: var(--ash); }
@keyframes flip { from { transform: rotateX(90deg); } }
.clues { margin: 0; padding: 0; list-style: none; display: grid; gap: var(--s-2); }
.clues li { padding: var(--s-2) var(--s-3); border-left: 3px solid var(--fluoro); background: var(--char); font-size: var(--t-md); }
.clues li b { font-family: var(--f-cond); color: var(--fluoro); margin-right: 6px; }
.history { font-size: var(--t-sm); color: var(--ash); text-align: center; }
.guessRow { display: grid; grid-template-columns: 1fr auto; gap: var(--s-2); }

/* ------------------------------------------------------------ suited up */

.ring { display: flex; justify-content: center; align-items: center; gap: 4px; flex-wrap: wrap; font-family: var(--f-cond); font-weight: 600; color: var(--ash); font-size: var(--t-sm); }
.ring svg { display: inline-block; }
.ringStep { display: inline-flex; align-items: center; gap: 4px; }
.suits { flex: 1; min-height: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 6px; max-height: 360px; }
.suit {
  display: grid;
  place-items: center;
  align-content: center;
  gap: var(--s-1);
  min-height: 88px;
  border: 1px solid var(--steel);
  background: var(--char);
  color: var(--chalk);
  font-family: var(--f-cond);
  font-weight: 700;
  font-size: var(--t-md);
  touch-action: manipulation;
}
.suit[aria-pressed='true'] { border: 2px solid var(--chalk); background: var(--slab-hi); }
.suits[data-chosen='true'] .suit[aria-pressed='false'] { opacity: 0.38; }
.suit small { color: var(--ash); font-weight: 600; font-size: var(--t-xs); }
.dealer { display: flex; justify-content: center; gap: var(--s-4); }
.card {
  width: 92px; height: 124px;
  display: grid; place-items: center;
  background: var(--paper-grain), linear-gradient(160deg, var(--paper-hi), var(--paper) 60%, var(--paper-shade));
  border: 1px solid var(--paper-shade);
  border-radius: 6px;
  animation: deal var(--m-slow) var(--ease) both;
}
.card:nth-child(2) { animation-delay: 140ms; }
@keyframes deal { from { transform: translateY(-20px) rotateY(90deg); opacity: 0; } }
.score { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; background: var(--steel); border: 1px solid var(--steel); text-align: center; }
.score > div { background: var(--char); padding: var(--s-2); display: grid; gap: 2px; }
.score b { font-family: var(--f-display); font-weight: 800; font-size: var(--t-2xl); }
.score span { font-family: var(--f-cond); color: var(--ash); font-size: var(--t-xs); font-weight: 600; }
.plus { color: var(--go); }
.minus { color: var(--stop); }

/* ------------------------------------------------------------ pressure cooker */

.gauge { position: relative; width: min(100%, 320px); margin: 0 auto; }
.gauge svg { width: 100%; height: auto; overflow: visible; }
.needle { transform-origin: 100px 100px; }
.mult {
  text-align: center;
  font-family: var(--f-display);
  font-weight: 800;
  font-size: clamp(3.6rem, 20vw, 5.5rem);
  line-height: 0.85;
  font-variant-numeric: tabular-nums;
  color: var(--sodium);
}
.mult[data-burst='true'] { color: var(--stop); animation: shake 380ms both; }
@keyframes shake { 20% { transform: translateX(-6px); } 40% { transform: translateX(6px); } 60% { transform: translateX(-3px); } 80% { transform: translateX(3px); } }
.cashBtn {
  min-height: 96px;
  width: 100%;
  border: 0;
  background: var(--sodium);
  color: var(--sodium-ink);
  font-family: var(--f-stencil);
  font-weight: 900;
  font-size: var(--t-4xl);
  letter-spacing: 0.04em;
  clip-path: polygon(0 0, calc(100% - 18px) 0, 100% 18px, 100% 100%, 18px 100%, 0 calc(100% - 18px));
  touch-action: manipulation;
}
.cashBtn:active:not(:disabled) { transform: scale(0.985); }
.cashBtn:disabled { opacity: 0.4; }
.ticker { display: flex; flex-wrap: wrap; gap: 6px; justify-content: center; min-height: 26px; }

/* ------------------------------------------------------------ speedrun */

.task { display: grid; gap: var(--s-3); padding: var(--s-4); background: var(--char); border: 1px solid var(--steel); border-top: 3px solid var(--g-speedrun); box-shadow: var(--edge); }
.taskHead { display: flex; justify-content: space-between; align-items: center; gap: var(--s-3); }
.taskPrompt { font-size: var(--t-xl); font-weight: 600; line-height: 1.25; }
.taskDetail { max-height: 30dvh; overflow: auto; font-size: var(--t-md); line-height: 1.5; color: var(--dust); white-space: pre-wrap; }
.taskDetail table { border-collapse: collapse; width: 100%; font-size: var(--t-sm); }
.taskDetail th, .taskDetail td { text-align: left; padding: 4px 8px 4px 0; border-bottom: 1px solid var(--steel); }
.taskDetail th { color: var(--ash); font-family: var(--f-cond); }
.symbols { font-size: var(--t-2xl); letter-spacing: 0.15em; text-align: center; color: var(--chalk); word-break: break-all; }
.choices { display: grid; gap: 6px; }
.choice { min-height: 52px; padding: 0 var(--s-4); border: 1px solid var(--steel); background: var(--slab); text-align: left; font-weight: 600; font-size: var(--t-lg); color: var(--chalk); touch-action: manipulation; }
.choice:active:not(:disabled) { background: var(--slab-hi); border-color: var(--g-speedrun); }
.choice[aria-pressed='true'] { border: 2px solid var(--g-speedrun); }
.clock { font-family: var(--f-display); font-weight: 800; font-size: var(--t-2xl); color: var(--g-speedrun); text-align: right; font-variant-numeric: tabular-nums; }

/* ------------------------------------------------------------ the flood: concrete platforms over rising water */

.floodHint { text-align: center; font-family: var(--f-cond); font-weight: 600; font-size: var(--t-lg); color: var(--dust); line-height: 1.25; }
.floodHint b { font-family: var(--f-display); font-weight: 800; font-size: var(--t-2xl); color: var(--g-flood); }
.platforms {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(var(--cols, 3), 1fr);
  grid-auto-rows: 1fr;
  gap: 6px;
  max-height: 440px;
  padding: 6px;
  /* the water the platforms stand in */
  background: linear-gradient(180deg, color-mix(in srgb, var(--g-flood-deep) 55%, transparent), var(--g-flood-deep));
  border: 1px solid var(--steel);
}
.platform {
  position: relative;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 2px;
  min-height: 48px;
  border: 1px solid var(--grit);
  background: linear-gradient(180deg, var(--slab-hi), var(--slab));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07), 0 3px 0 #0d0d0c;
  color: var(--chalk);
  touch-action: manipulation;
  overflow: hidden;
  transition: transform var(--m-fast) var(--ease), opacity var(--m-fast), border-color var(--m-fast);
}
.platform:active:not(:disabled) { transform: translateY(2px); }
.platformNo { position: relative; font-family: var(--f-display); font-weight: 800; font-size: var(--t-2xl); line-height: 1; }
/* a dried tide line where last tide's water stood */
.platform[data-tideline]::after { content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: 26%; background: linear-gradient(0deg, color-mix(in srgb, var(--g-flood) 22%, transparent), transparent); border-top: 1px dashed color-mix(in srgb, var(--g-flood) 55%, transparent); }
.tideNote { position: relative; z-index: 1; font-family: var(--f-cond); font-weight: 600; font-size: var(--t-xs); color: var(--g-flood); }
.platform[aria-pressed='true'] { border: 2px solid var(--chalk); background: linear-gradient(180deg, var(--steel), var(--slab-hi)); }
.platforms[data-chosen='true'] .platform[aria-pressed='false'] { filter: brightness(0.45) saturate(0.6); } /* dim, but never translucent: see-through slabs would read as flooded */
.platformsReveal { flex: none; max-height: 240px; }
.platformsReveal .platform { min-height: 40px; }
.platform[data-wet]::before {
  content: '';
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(170deg, transparent 0 7px, rgba(255, 255, 255, 0.08) 7px 9px), linear-gradient(180deg, color-mix(in srgb, var(--g-flood) 70%, transparent), var(--g-flood-deep));
  animation: rise var(--m-slow) var(--ease) both;
  transform-origin: bottom;
}
@keyframes rise { from { transform: scaleY(0); } }
.platform[data-wet] .platformNo { color: var(--chalk); opacity: 0.7; }
.platform[data-mine] { border: 2px solid var(--chalk); }
.occupancy { position: relative; font-family: var(--f-cond); font-weight: 700; font-size: var(--t-xs); color: var(--dust); }
.platform[data-wet] .occupancy { color: var(--chalk); }

/* ------------------------------------------------------------ wrong way: a junction seen from above */

.wayHint { display: grid; justify-items: center; gap: 2px; text-align: center; font-family: var(--f-cond); font-weight: 600; font-size: var(--t-lg); color: var(--dust); line-height: 1.2; }
.wayHint b { font-family: var(--f-display); font-weight: 800; font-size: var(--t-2xl); color: var(--g-wrong-way); }
.wayLine { display: block; }
.wayField { font-size: var(--t-sm); color: var(--ash); }
.wayFrame {
  flex: 1;
  min-height: 0;
  max-height: 420px;
  display: grid;
  padding: 8px;
  /* corridor floor and walls */
  background:
    linear-gradient(90deg, transparent 33%, rgba(0, 0, 0, 0.35) 33% 34%, transparent 34% 66%, rgba(0, 0, 0, 0.35) 66% 67%, transparent 67%),
    var(--grain),
    var(--char);
  border: 1px solid var(--steel);
}
.junction {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 1fr 1fr 1fr;
  grid-template-areas: '. up .' 'left hub right' '. down .';
  gap: 6px;
  min-height: 0;
}
.junction[data-routes='5'] { grid-template-areas: '. up .' 'left through right' '. down .'; }
.junctionSlot { display: grid; min-height: 0; }
.junctionSlot[data-route='left'] { grid-area: left; }
.junctionSlot[data-route='up'] { grid-area: up; }
.junctionSlot[data-route='right'] { grid-area: right; }
.junctionSlot[data-route='down'] { grid-area: down; }
.junctionSlot[data-route='through'] { grid-area: through; }
.junctionHub { grid-area: hub; display: grid; place-items: center; }
.junctionHub span { display: grid; place-items: center; width: 54px; height: 54px; border-radius: 50%; border: 2px dashed var(--grit); font-family: var(--f-cond); font-weight: 700; color: var(--ash); }
.door {
  display: grid;
  place-items: center;
  align-content: center;
  gap: 4px;
  min-height: 56px;
  border: 1px solid var(--grit);
  border-top: 3px solid var(--g-wrong-way);
  background: linear-gradient(180deg, var(--slab-hi), var(--slab));
  box-shadow: var(--edge);
  color: var(--chalk);
  font-family: var(--f-cond);
  font-weight: 700;
  font-size: var(--t-md);
  touch-action: manipulation;
  transition: transform var(--m-fast) var(--ease), filter var(--m-fast), border-color var(--m-fast);
}
.door:active:not(:disabled) { transform: translateY(1px) scale(0.98); }
.junctionSlot[data-route='through'] .door { border-radius: 50%; border: 2px solid var(--g-wrong-way); background: radial-gradient(circle, var(--void) 30%, var(--slab) 72%); }
.door[aria-pressed='true'] { border-color: var(--chalk); border-top-color: var(--chalk); background: linear-gradient(180deg, var(--steel), var(--slab-hi)); }
.wayFrame[data-chosen='true'] .door[aria-pressed='false'] { filter: brightness(0.45); }
.wayReveal { flex: none; max-height: 260px; }
.wayReveal .door { min-height: 60px; gap: 0; }
.wayReveal .door b { font-family: var(--f-display); font-weight: 800; font-size: var(--t-xl); line-height: 1; }
.door[data-safe] { border-color: var(--go-dim); border-top-color: var(--go); color: var(--go); }
.door[data-dead] { border-color: var(--stop-dim); border-top-color: var(--stop); color: var(--stop); background: repeating-linear-gradient(-45deg, transparent 0 8px, rgba(238, 75, 59, 0.08) 8px 10px), var(--char); }
.door[data-mine] { outline: 2px solid var(--chalk); outline-offset: 2px; }

/* ------------------------------------------------------------ reveal blocks */

.reveal { flex: 1; min-height: 0; display: flex; flex-direction: column; gap: var(--s-4); justify-content: center; }
.revealHead { text-align: center; display: grid; gap: var(--s-2); justify-items: center; }
.big { font-family: var(--f-display); font-weight: 800; font-size: var(--t-3xl); text-transform: uppercase; line-height: 0.95; }
.list { display: grid; gap: 2px; max-height: 32dvh; overflow: auto; }
.listRow { display: grid; grid-template-columns: 1fr auto; gap: var(--s-3); padding: 6px 0; border-bottom: 1px solid var(--char); font-size: var(--t-sm); }
.listRow b { font-weight: 600; }
.ok { color: var(--go); }
.bad { color: var(--stop); }
